import { useEffect, useState } from "react";
import { CategoryWithParts, IPart } from "../../../../models/Part";
import PriceFormat from "../../../../utils/priceFormat";
import { formatMinutesIntoHours } from "../../../../utils/formatMinutesIntoHours";
import { ButtonInput } from "../../../shared/Inputs/ButtonInput";
import { CreateJobRequest, JobType } from "../../../../models/Job";
import { ILead } from "../../../../models/Lead";
import { IVehicle } from "../../../../models/Vehicle";
import useDefaultCRUDHandlers from "../../../../hooks/useDefaultCRUDHandlers";
import useJobMutations from "../../../../data/useJobMutations";
import usePartMutations from "../../../../data/usePartMutations";
import { DateTime } from "luxon";
import useSingleSetting from "../../../../data/useSingleSetting";
import useRsAutomotive from "../../../../data/useRsAutomotive";


const SummaryCard = ({ title, time, price, total }: { 
  title: string, 
  time?: number | null, 
  price: number,
  total?: number | null
}) => (
  <div className="bg-white shadow rounded-lg p-4 w-full">
    <h1 className="text-lg font-semibold">{title}</h1>
    <div className="flex justify-between">
      {time && (
        <div>
          <p className="text-gray-600 text-sm">Est. Time</p>
          <p>{formatMinutesIntoHours(time)}</p>
        </div>
      )}
      {total && (
        <div>
          <p className="text-gray-600 text-sm">Total</p>
          <p>{total}</p>
        </div>
      )}
      <div>
        <p className="text-gray-600 text-sm">Price</p>
        <p>{PriceFormat(price)}</p>
      </div>
    </div>
  </div>
);

interface SelectedCategoriesProps {
  selectedCategories: CategoryWithParts[] | undefined;
  setSelectedCategories: any;
  setSelectedByTableId: any;
  lead: ILead | null;
  vehicle: IVehicle | null;
}

export default function SelectedCategories({ selectedCategories, lead, vehicle, setSelectedCategories, setSelectedByTableId }: SelectedCategoriesProps) {
  const { create } = useJobMutations();
  const { createParts } = usePartMutations();
  const { saveHandlers } = useDefaultCRUDHandlers("Job");
  const { saveHandlers: partsSaveHandlers } = useDefaultCRUDHandlers("Parts");
  const { singleSetting } = useSingleSetting("Rs:ApiKey");
  const { order } = useRsAutomotive();
  const [hasRsKey, setHasRsKey] = useState(false);
  const [rsApiKey, setRsApiKey] = useState<string | null>(null);
  const [totalTime, setTotalTime] = useState<number>(0);
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [bodyTime, setBodyTime] = useState<number>(0);
  const [mechTime, setMechTime] = useState<number>(0);
  const [bodyPrice, setBodyPrice] = useState<number>(0);
  const [mechPrice, setMechPrice] = useState<number>(0);
  
  const [rsTotal, setRsTotal] = useState<number>(0);
  const [rsPrice, setRsPrice] = useState<number>(0);


  useEffect(() => {
    if (!selectedCategories || selectedCategories.length === 0) {
      return;
    }

    // Calculate total time and price
    setTotalTime(selectedCategories.map(cat => cat.category.whTime! + cat.category.disTime!).reduce((a, b) => a! + b!)!);
    setTotalPrice(selectedCategories.map(cat => cat.price!).reduce((a, b) => a! + b!)!);

    // Calculate body work time and price
    setBodyTime(selectedCategories.map(cat => !cat.isMech! ? cat.category.whTime! + cat.category.disTime! : 0).reduce((a, b) => a! + b!)!);
    setBodyPrice(selectedCategories.map(cat => !cat.isMech! ? cat.price! : 0).reduce((a, b) => a! + b!)!);

    // Calculate mechanical work time and price
    setMechTime(selectedCategories.map(cat => cat.isMech! ? cat.category.whTime! + cat.category.disTime! : 0).reduce((a, b) => a! + b!)!);
    setMechPrice(selectedCategories.map(cat => cat.isMech! ? cat.price! : 0).reduce((a, b) => a! + b!)!);

    setRsTotal(selectedCategories.filter(cat => cat.isCore).length);
    setRsPrice(selectedCategories.map(cat => cat.isCore ? cat.category.rsPrice : 0).reduce((a, b) => a! + b!)!);

  }, [selectedCategories]);

  useEffect(() => {
    setHasRsKey(singleSetting.isLoading ? false : !!singleSetting.data?.data?.value);
    setRsApiKey(singleSetting.isLoading ? null  : singleSetting.data?.data?.value ?? null);
  }, [singleSetting.data, singleSetting.isLoading]);

  const handleCreateJob = async () => {

    if (!selectedCategories || selectedCategories.length === 0) return;

    let rsRes = await handleRsOrder();
    if (rsRes != null && rsRes.rs_status != "success") {
      saveHandlers.onError();
      return;
    }

    const newJob: CreateJobRequest = {
      type: JobType.Generic, //All jobs are generic now
      title: `${vehicle?.combinedMake} ${vehicle?.modelRangeDescription} - ${vehicle?.vrm}`,
      description: selectedCategories.map(cat => cat.category.name).join(", "),
      duration: totalTime,
      estimatedPrice: totalPrice, //If everything sells at the price entered
      estimatedMargin: null, //I don't think this is useful anymore as we don't have it for new parts
      leadId: lead?.id ?? null,
      vehicleId: vehicle?.id,
      jobPartCategories: selectedCategories.map(cat => ({
        partCategory: cat.category,
        averagePrice: cat.category.averagePrice,
        price: (cat.isCore ? cat.category.rsPrice : cat.price) ?? 0,
        lastSoldPrice: cat.lastSoldPrice ?? cat.category.lastSoldPrice,
        note: cat.note,
        coreOrderNumber: cat.isCore ? rsRes.rs_data.rs_sales_order_number : null
      })),
    }

    // console.log("new Job", newJob)

    create.mutate(newJob, {
      ...saveHandlers,
      onSuccess: () => {
        setSelectedCategories([]);
        setSelectedByTableId({});
        saveHandlers.onSuccess();
      }
    });
  }

  const handleRsOrder = async () => {
    if (!selectedCategories || selectedCategories.length === 0) return null;

    var rsCategories = selectedCategories.filter(sc => sc.isCore);
    if (!rsCategories || rsCategories.length === 0) return null;

    console.log("-------- RS Categories --------");
    console.log(rsCategories);

    var res = await order.mutateAsync({
      apiKey: rsApiKey, 
      payload: { 
        rs_ktype:  rsCategories[0].category.rsData.rs_ktype,
        rs_regno:  rsCategories[0].category.rsData.rs_regno,
        rs_search: rsCategories[0].category.rsData.rs_search,
        rs_vendor: rsCategories[0].category.rsData.rs_vendor,
        rs_products: Object.fromEntries(
          rsCategories.map((rc) => {
            const categoryName = (rc.category.rsName ?? rc.category.name)?.trim();
            const rsItem = rc.category.rsItem;

            // skip invalid rows
            if (!categoryName || !rsItem) return null;

            return [categoryName, { rs_item: rsItem }] as const;
          }).filter(Boolean) as Array<readonly [string, { rs_item: string }]>
        ),
      }
    });

    return res;
  }

  const handleCreateParts = async (isDamaged: boolean) => {
    if (!selectedCategories || selectedCategories.length === 0) return;

    let rsRes = await handleRsOrder();
    if (rsRes != null && rsRes.rs_status != "success") {
      saveHandlers.onError();
      return;
    }

    const newParts = selectedCategories.map(cat => ({
      categoryId: cat.category.id,
      price: (cat.isCore ? cat.category.rsPrice : cat.price) ?? 0,
      averagePrice: cat.category.averagePrice,
      lastSoldPrice: cat.lastSoldPrice ?? cat.category.lastSoldPrice,
      note: cat.note,
      coreOrderNumber: cat.isCore ? rsRes.rs_data.rs_sales_order_number : null,
      isDamaged
    }));

    const body = {
      vehicleId: vehicle?.id,
      leadId: lead?.id,
      parts: newParts
    }

    // console.log("new parts", newParts);

    createParts.mutate(body, {
      ...partsSaveHandlers,
      onSuccess: () => {
        setSelectedCategories([]);
        setSelectedByTableId({});
        saveHandlers.onSuccess();
      }
    });
  }

  if (!selectedCategories || selectedCategories.length === 0) {
    return null;
  };

  return (
    <>
      <div className="flex flex-col sticky pb-2 top-14 z-30 bg-gray-100">

        <div className="flex items-center gap-x-8 mt-4">

          <div className="bg-white shadow rounded-lg p-4 w-full">
            <h1 className="text-lg font-semibold">Categories Selected</h1>
            <p className="text-2xl pt-3 flex items-center gap-x-4">{selectedCategories.length}
              {selectedCategories.some(cat => !cat.price) && (
                <span className="text-gray-600 text-sm"> - {selectedCategories.filter(cat => !cat.price).length} categories have no price</span>
              )}
            </p>
          </div>

          <SummaryCard title="Total" time={totalTime} price={totalPrice} />
          <SummaryCard title="Body Work" time={bodyTime} price={bodyPrice} />
          <SummaryCard title="Mechanical Work" time={mechTime} price={mechPrice} />
          {hasRsKey && (
            <SummaryCard title="RS Automotive" total={rsTotal} price={rsPrice} />
          )}
        </div>

      </div>
      <div className="flex justify-between">
        <div className="flex gap-x-6">
          <ButtonInput label={`${lead?.jobs && lead.jobs.length > 0 ? "Update" : "Create"} Job`} onClick={() => handleCreateJob()} classes={undefined} isLoading={create.isLoading} />
          <ButtonInput label={"Make Available"} onClick={() => handleCreateParts(false)} classes={undefined} isLoading={createParts.isLoading} />
        </div>

        <div>
          <ButtonInput label={"Mark as Damaged"} onClick={() => handleCreateParts(true)} classes={"!bg-red-500 hover:!bg-red-600"} isLoading={createParts.isLoading} />
        </div>
      </div>
    </>
  )
}